import { createContext, useMemo, type ReactNode } from 'react';
import { createHttpClient, type HttpClient } from './client/http';
import { useAuthState, type AuthState } from './auth/state';
import { createAuthMethods, type AuthMethods } from './auth/methods';
import { createCollection } from './db/collection';
import { createShared } from './db/shared';
import { createPublicCatalog } from './db/public-catalog';
import { createAppData } from './appData';
import { AppDataCache } from './appDataCache';
import { createUpload } from './upload';
import { createStorage, type Storage } from './storage';
import { useSubscriptionState, type SubscriptionState } from './subscription/state';
import { usePlanState, type PlanState } from './subscription/plan';
import { createPush, type PushMethods } from './push/push';
import { createAiClient, type AiClient } from './ai';
import { createTrack } from './stubs/track';

export interface HookConfig {
  appId: string;
  /**
   * URL-safe slug for this app (e.g. `papomaterno`). Used by OAuth `/start`
   * to carry the tenant through the Google redirect via a signed `state` JWT,
   * so a single centralized redirect URI routes back to the correct app.
   */
  appSlug: string;
  apiHost: string;
  /**
   * Absolute URL prefix for this app's static assets (images, fonts, videos).
   * Set by the shell's AppLoader to `${r2Host}/${slug}/assets/`. Bundles should
   * never inline large assets as base64 — instead, emit them to `dist/assets/**`
   * during build (CI uploads alongside app.js) and resolve the URL at runtime
   * via the `useAsset()` hook. One universal bundle works in any environment
   * because the host is injected, not compiled.
   *
   * See docs/04-bundle-pipeline.md § "Assets estáticos do app".
   */
  assetsHost: string;
}

export interface HookContextValue extends AuthState {
  /** Echo back of the HookConfig for consumers that need it (e.g. useAsset). */
  config: HookConfig;
  auth: AuthMethods;
  db: {
    collection: ReturnType<typeof createCollection>;
    shared: ReturnType<typeof createShared>;
  };
  /**
   * P-CAT-PUB — read-only catalog público (sem auth, opt-in via
   * `app_shared_collections.is_public=true`). Funciona pre-auth para
   * wizards de onboarding mostrarem catálogo antes do signup.
   */
  publicCatalog: ReturnType<typeof createPublicCatalog>;
  appData: ReturnType<typeof createAppData>;
  upload: ReturnType<typeof createUpload>;
  storage: Storage;
  subscription: SubscriptionState;
  /**
   * Plano de assinatura do app, read-only, sem auth. Populado em paralelo
   * com o resto do provider — disponível pra telas pre-auth (Anchor/Offer)
   * lerem preço do banco em vez de hardcodar. Ver `usePlanState`.
   */
  plan: PlanState;
  push: PushMethods;
  /**
   * P16 — AI proxy task-based. Substitui `hook.fetch()` (descontinuado).
   * Ver ADR-021 + docs/11-security.md §"Proxy de IA".
   */
  ai: AiClient;
  track: ReturnType<typeof createTrack>;
}

export const HookContext = createContext<HookContextValue | undefined>(undefined);

export function HookProvider({ config, children }: { config: HookConfig; children: ReactNode }) {
  // Memoiza o client por (appId, apiHost) — reset apenas se props mudam.
  const client: HttpClient = useMemo(
    () => createHttpClient({
      apiHost: config.apiHost,
      onUnauthorized: () => { /* state.refresh já marca anonymous */ },
    }),
    [config.apiHost, config.appId],
  );

  const authState = useAuthState(client, config.appId);
  const auth = useMemo(
    () => createAuthMethods({
      client,
      appId: config.appId,
      appSlug: config.appSlug,
      apiHost: config.apiHost,
      refreshUser: authState.refresh,
    }),
    [client, config.appId, config.appSlug, config.apiHost, authState.refresh],
  );

  const subscription = useSubscriptionState(client, config.appId, authState.user?.id ?? null);
  const plan = usePlanState(client, config.appId);

  // Cache de app_data memoizado por (appId, apiHost) junto do client. Uma
  // instância por mount do provider: remount (ex.: app swap no shell) reseta
  // o cache naturalmente, o que é o lifecycle que queremos.
  const appDataCache = useMemo(() => new AppDataCache(), [client]);

  const value = useMemo<HookContextValue>(
    () => ({
      ...authState,
      config,
      auth,
      db: {
        collection: createCollection(client, config.appId),
        shared: createShared(client, config.appId),
      },
      publicCatalog: createPublicCatalog(client, config.appId),
      appData: createAppData(client, config.appId, appDataCache),
      upload: createUpload(client, config.appId, () => authState.user?.id ?? null),
      storage: createStorage(config.appId, () => authState.user?.id ?? null),
      subscription,
      plan,
      push: createPush(client, config.appId),
      ai: createAiClient({ apiHost: config.apiHost, appId: config.appId }),
      track: createTrack({ appId: config.appId, appSlug: config.appSlug }),
    }),
    [client, config, authState, auth, subscription, plan, appDataCache],
  );

  return <HookContext.Provider value={value}>{children}</HookContext.Provider>;
}
